import { definePreset } from "@primeuix/themes";
import PrimeTheme from "@primeuix/themes/aura";
import { createCookies } from "@vueuse/integrations/useCookies";
import * as devalue from "devalue";
import dotenv from "dotenv";
import type express from "express";
import { createPinia } from "pinia";
import { FocusTrap, ToastService, Tooltip } from "primevue";
import PrimeVue, { type PrimeVueLocaleOptions } from "primevue/config";
import DialogService from "primevue/dialogservice";
import { createSSRApp } from "vue";
import { createMemoryHistory, createRouter, createWebHistory } from "vue-router";
import { routes } from "./data/routes";
import RootApp from "./RootApp.vue";
import { BreadcrumbItem, useBreadcrumbStore } from "./stores/breadcrumb.store";
import { useEnvironmentStore } from "./stores/environment.store";
import { useMenuStore } from "./stores/menu.store";
import { NAV, updateCrumbs } from "./stores/navigation.store";
import { useProfileStore } from "./stores/profile.store";
import { useTokenStore } from "./stores/token.store";
import "./style/main.css";

function setupRouter() {
    if (import.meta.env.SSR) {
        return createRouter({
            history: createMemoryHistory(),
            routes: routes,
        });
    } else {
        return createRouter({
            history: createWebHistory(),
            routes: routes,
        });
    }
}

export async function setupApp(url: string, req?: express.Request) {
    const pinia = createPinia();
    const router = setupRouter();

    const MyPreset = definePreset(PrimeTheme, {
        semantic: {
            primary: {
                50: "var(--color-brand-50)",
                100: "var(--color-brand-100)",
                200: "var(--color-brand-200)",
                300: "var(--color-brand-300)",
                400: "var(--color-brand-400)",
                500: "var(--color-brand-500)",
                600: "var(--color-brand-600)",
                700: "var(--color-brand-700)",
                800: "var(--color-brand-800)",
                900: "var(--color-brand-900)",
                950: "var(--color-brand-900)",
            },
            borderRadius: {
                none: "0",
                xs: "4px",
                sm: "8px",
                md: "16px",
                lg: "20px",
                xl: "24px",
            },
            colorScheme: {
                light: {
                    surface: {
                        0: "var(--color-surface)",
                        50: "var(--color-paper)",
                        100: "var(--color-hovered)",
                        500: "var(--color-ink-faint)",
                        600: "var(--color-ink-mute)",
                        700: "var(--color-ink-soft)",
                        800: "var(--color-ink-soft)",
                        900: "var(--color-ink)",
                    },
                    formField: {
                        background: "var(--color-surface)",
                        disabledBackground: "var(--color-rule)",
                        borderColor: "var(--color-rule)",
                        hoverBorderColor: "var(--color-ink-faint)",
                        focusBorderColor: "var(--color-brand-500)",
                        color: "var(--color-ink)",
                        disabledColor: "var(--color-ink-faint)",
                    },
                },
                dark: {
                    surface: {
                        0: "var(--color-surface)",
                        50: "var(--color-paper)",
                        100: "var(--color-hovered)",
                        500: "var(--color-ink-faint)",
                        600: "var(--color-ink-mute)",
                        700: "var(--color-ink-soft)",
                        800: "var(--color-ink-soft)",
                        900: "var(--color-ink)",
                    },
                    formField: {
                        background: "var(--color-surface)",
                        disabledBackground: "var(--color-rule)",
                        borderColor: "var(--color-line, var(--color-rule))",
                        hoverBorderColor: "var(--color-ink-faint)",
                        focusBorderColor: "var(--color-brand-500)",
                        color: "var(--color-ink)",
                        disabledColor: "var(--color-ink-faint)",
                    },
                },
            },
        },
        components: {
            button: {
                root: {
                    borderRadius: "9999px",
                    paddingX: "calc(var(--spacing) * 5)",
                    paddingY: "calc(var(--spacing) * 2)",
                },
            },
        },
    });

    const app = createSSRApp(RootApp)
        .use(router)
        .use(pinia)
        .use(PrimeVue, {
            theme: {
                preset: MyPreset,
                options: {
                    darkModeSelector: false,
                },
            },
            locale: {
                emptyFilterMessage: "Hasil tidak ditemukan",
                emptyMessage: "Tidak ada opsi tersedia",
                noFilter: "Tanpa Filter",
                monthNames: [
                    "Januari",
                    "Februari",
                    "Maret",
                    "April",
                    "Mei",
                    "Juni",
                    "Juli",
                    "Agustus",
                    "September",
                    "Oktober",
                    "November",
                    "Desember",
                ],
                monthNamesShort: [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "Mei",
                    "Jun",
                    "Jul",
                    "Agu",
                    "Sep",
                    "Okt",
                    "Nov",
                    "Des",
                ],
                dayNames: ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"],
                dayNamesShort: ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"],
                dayNamesMin: ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"],
                firstDayOfWeek: 1,
            } as const satisfies Partial<PrimeVueLocaleOptions>,
        })
        .use(DialogService)
        .use(ToastService)
        .directive("tooltip", Tooltip)
        .directive("focustrap", FocusTrap);

    if (import.meta.env.SSR) {
        const tokenCookies = createCookies(req)(["token"]);
        const token = tokenCookies.get("token") ?? undefined;

        dotenv.config();
        const environmentStore = useEnvironmentStore(pinia);
        environmentStore.inject(
            process.env.API_ENDPOINT as string,
            process.env.ASSETS_ENDPOINT as string,
            Buffer.from(process.env.ZH_ENDPOINT as string, "utf-8").toString("base64"),
        );

        if (
            url.match(
                /^\/(privacy-policy-employee|terms-of-service-employee|mobile-support-employee)(?:\/(.+)?)?/,
            )
        ) {
            await router.push(url);
        } else if (token !== undefined) {
            const tokenStore = useTokenStore(pinia);
            const profileStore = useProfileStore(pinia);
            tokenStore.inject(token);

            try {
                await profileStore.synchronize();
                await router.push(url);
            } catch (_) {
                tokenStore.clear();
                await router.push("/signin");
            }
        } else {
            await router.push("/signin");
        }
    } else {
        pinia.state.value = devalue.parse(window.__pinia);
        const tokenStore = useTokenStore(pinia);

        if (
            url.match(
                /^\/(privacy-policy-employee|terms-of-service-employee|mobile-support-employee)(?:\/(.+)?)?/,
            )
        ) {
            await router.push(url);
        } else if (!tokenStore.isValid()) {
            await router.push("/signin");
        }
    }

    router.afterEach((to) => {
        const routeCrumbs = to.meta?.crumbs as BreadcrumbItem[] | undefined;
        const breadcrumb = useBreadcrumbStore();

        if (routeCrumbs && routeCrumbs.length > 0) {
            breadcrumb.setCrumbs(routeCrumbs);
            return;
        }

        const menuStore = useMenuStore();
        if (menuStore.currentMenu) {
            const found = updateCrumbs(NAV.value, menuStore.currentMenu);
            if (found) return;
        }

        breadcrumb.resetCrumbs();
    });

    await router.isReady();

    return { app: app, router: router, pinia: pinia };
}

declare global {
    interface Window {
        __pinia: any;
    }
}
